import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Box, Button, Divider, IconButton, Tooltip } from "@mui/material";

import { EmissionFactorReferencesIcon, EsgDeleteIcon, EsgNotesIcon } from "assets";
import { deleteSiteById, SiteByIdDTO, SiteDTO } from "api";
import { useGetAllowedValues, useNotify, useSiteById, useSites } from "hooks";
import { getAllowedValuesHelper, convertBlankStringsToNull, getLoggedUserData } from "utils";
import { COLORS, CustomMaterialTable, DeleteDialog, Loading, ReturnButton, SwitchToActivation, useDialogControls } from "components";

import { SITES_PAGE, siteDetailsColumns } from "./consts";
import { SiteNameDetail, SiteEditorForm } from "./components";
import { AddSiteModal } from "./components/site-editor-form/components/AddSiteModal";
import { SiteColumns } from "./components/site-editor-form/components/SiteColumns";

export const SiteDetailPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const notify = useNotify();
  const deleteDialogControls = useDialogControls();
  const loggedUserData = getLoggedUserData();

  const { data: allowedValues } = useGetAllowedValues();
  const { data: sitesData, isLoading, refetch } = useSites();
  const { data: siteByIdData, isLoading: isSiteByIdLoading } = useSiteById(Number(params.siteId));
  const { mutateAsync: deleteSiteRequest } = useMutation(deleteSiteById);

  const currentSite = useMemo(() => ({
    ...sitesData?.find((site: SiteDTO) => String(site.id) === params.siteId),
    collectors: siteByIdData?.collectors || [],
  }), [sitesData, params, siteByIdData]);

  const userAdminLogged = useMemo(() => loggedUserData.roles?.includes("MODULE_ADMIN") ?? {}, [loggedUserData]);

  const [addSiteModalOpen, setAddSiteModalOpen] = useState(false);
  const [editSiteOpen, setEditSiteOpen] = useState(false);
  const [currentSiteData, setCurrentSiteData] = useState<any | undefined>();
  const [clickType, setClickType] = useState<string>("");
  const [editNotesOpen, setEditNotesOpen] = useState<boolean>(false);
  const [toDeleteSite, setDeleteSite] = useState(false);

  const handleEmissionFactorReference = (id: number | string) => {
    navigate(`/references?s=${id}`)
  }

  const handleCloseSiteModal = () => {
    if (addSiteModalOpen) {
      setAddSiteModalOpen(false);
    }
    if (editSiteOpen) {
      setEditSiteOpen(false);
    }
    if (editNotesOpen) {
      setEditNotesOpen(false);
    }
  };

  const handleEditSite = (rowData: any, type: string) => {
    setClickType(type);
    setEditSiteOpen(true);
    setCurrentSiteData(rowData);
  };

  const handleEditSiteNotes = (rowData: any, type: string) => {
    setClickType(type)
    setEditNotesOpen(true);
    setCurrentSiteData(rowData);
  };

  const onDeleteClick = async (siteId: number | undefined) => {
    if (siteId) {
      try {
        await deleteSiteRequest(siteId, {
          onSuccess: () => {
            deleteDialogControls.close();
            notify.success(`Site successfully deleted: ${currentSite && currentSite.name}!`);
            navigate(SITES_PAGE.ABSOLUTE_PATH);
            refetch();
          },
        });
      } catch (error: any) {
        notify.error(error?.response?.data && typeof error?.response?.data === "string" ? error?.response?.data : `Some error has happen while deleting site: ${currentSite && currentSite.name}!`);
        deleteDialogControls.close();
      }
    }
  };

  return (
    isLoading || isSiteByIdLoading
      ? <Loading />
      : (<Box sx={{ mt: 1, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3 }}>
          <ReturnButton
            label="Back to Site Management"
            onClick={() => navigate(-1)}
          />
          <Button
            sx={{
              width: "140px",
              height: "36px",
              color: "#E21515",
              bgcolor: "transparent",
              borderRadius: "8px",
              borderColor: "#E21515",
              fontSize: "14px",
              fontWeight: "500",
            }}
            disabled={!userAdminLogged || !currentSite?.enabled}
            onClick={() => {
              deleteDialogControls.open();
              setDeleteSite(true);
            }}
            startIcon={<EsgDeleteIcon />}
            variant="outlined"
          >
            Delete site
          </Button>
        </Box>
        <Box sx={{ my: 2, px: 3 }}>
          <CustomMaterialTable
            columns={SiteColumns}
            data={currentSite ? [currentSite] : []}
            enableTopToolbar={false}
            enableRowVirtualization={false}
            rowClickable
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleEditSite(convertBlankStringsToNull(row.original), ""),
            })}
            muiTableContainerProps={{
              sx: {
                width: "calc(100vw - 150px)",
                "& tbody tr td": {
                  paddingY: "0px",
                },
                "& thead tr:first-of-type th:first-of-type": {
                  borderRight: "1px solid #e0e0e0",
                },
                "& tbody tr:first-of-type td:first-of-type": {
                  borderRight: "1px solid #e0e0e0",
                  paddingLeft: "16px",
                },
              },
            }}
            muiTableBodyCellProps={({ row }) => ({
              sx: {
                color: row.original?.enabled ? "black" : "grey",
                backgroundColor: "#F8FAFC",
              },
            })}
          />
        </Box>
        <Divider sx={{ mt: 3, borderColor: "#d9d9d9" }} />
        <Box sx={{ px: 3 }}>
          <p style={{ fontSize: "16px", fontWeight: "500" }}>Site Details</p>
          <CustomMaterialTable
            data={currentSite ? [currentSite] : []}
            columns={siteDetailsColumns}
            enableTopToolbar={false}
            enableRowVirtualization={false}
            rowClickable
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleEditSite(convertBlankStringsToNull(row.original), ""),
            })}
            muiTableContainerProps={{
              sx: {
                width: "calc(100vw - 150px)",
                "& tbody tr td": {
                  paddingY: "0px",
                },
                "& thead tr:first-of-type th:first-of-type": {
                  borderRight: "1px solid #e0e0e0",
                },
                "& tbody tr:first-of-type td:first-of-type": {
                  borderRight: "1px solid #e0e0e0",
                  paddingLeft: "16px",
                },
              },
            }}
            muiTableBodyCellProps={({ row }) => ({
              sx: {
                color: row.original?.enabled ? "black" : "grey",
                fontSize: "14px",
              },
            })}
            muiTableHeadCellProps={{
              sx: {
                verticalAlign: "center",
                paddingBottom: "0px",
              },
            }}
            enableRowActions
            initialState={{ density: "compact" }}
            renderRowActions={({ row }) => (
              <Box
                sx={{
                  height: "45px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box sx={{ pr: "8px" }}>
                  <SwitchToActivation
                    enabled={row.original.enabled}
                    textOn="Deactivate"
                    textOff="Activate"
                    onChange={() => handleEditSite(convertBlankStringsToNull(row.original), row.original.enabled ? "Deactivate" : "Enable")}
                  />
                </Box>
                <Tooltip arrow placement="top" title="Notes">
                  <IconButton
                    sx={{ width: "35px", height: "35px" }}
                    onClick={() => handleEditSiteNotes(convertBlankStringsToNull(row.original), "")}
                  >
                    <EsgNotesIcon color={row.original.notes !== null ? COLORS.Romance : "grey"} />
                  </IconButton>
                </Tooltip>
                <Tooltip arrow placement="top" title="Emission Factor References">
                  <IconButton
                    sx={{ width: "35px", height: "35px" }}
                    onClick={() => handleEmissionFactorReference(row.original?.id)}
                  >
                    <EmissionFactorReferencesIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          />
        </Box>
        <Box>
          {siteByIdData && (
            <SiteEditorForm
              allowedValues={getAllowedValuesHelper(allowedValues)}
              site={siteByIdData || ({} as SiteByIdDTO)}
              toDeleteSite={toDeleteSite}
            />)}
        </Box>
        <AddSiteModal
          isOpen={addSiteModalOpen || editSiteOpen || editNotesOpen}
          onClose={handleCloseSiteModal}
          editNotes={editNotesOpen}
          allowedValues={getAllowedValuesHelper(allowedValues)}
          siteData={currentSiteData}
          clickType={clickType}
        />
        <DeleteDialog
          isOpen={deleteDialogControls.isOpen}
          onCancel={deleteDialogControls.close}
          onDeleteClick={() => onDeleteClick(currentSite?.id)}
          target={<SiteNameDetail name={currentSite?.name} />}
          title="Delete Site?"
        />
      </Box>)
  )
}